import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";

function buildDates() {
  const out = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (out.length < 10) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}
const TIMES = ["10:00", "12:00", "16:00", "18:00"];

export default function Booking() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const locale = { en: "en-US", es: "es-ES", fr: "fr-FR", de: "de-DE", it: "it-IT", pt: "pt-PT" }[lang] || "en-US";
  const dates = useMemo(buildDates, []);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", idea: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name && form.email && form.phone && form.idea && date && time;
  const fmtDate = (d) =>
    d.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });

  const submit = async () => {
    if (!valid) {
      toast.error(t("booking.errorRequired"));
      return;
    }
    setLoading(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        ...form,
        date: date.toISOString().slice(0, 10),
        time,
        language: lang,
      });
      const { data: checkout } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  return (
    <div data-testid="booking-page" className="min-h-screen bg-white">
      <div className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        <button
          data-testid="booking-back-btn"
          onClick={() => navigate("/")}
          className="mb-12 inline-flex items-center gap-2 text-sm font-light text-[#4a4a4a] transition-colors hover:text-[#050505]"
        >
          <ArrowLeft size={16} /> {t("booking.back")}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="overline text-[#0e7c6b]">{t("booking.overline")}</p>
          <h1 className="font-display mt-6 text-4xl font-light leading-[1.02] tracking-tightest text-[#050505] md:text-6xl">
            {t("booking.title")}
          </h1>
          <p className="mt-5 text-lg font-light text-[#4a4a4a]">{t("booking.subtitle")}</p>
        </motion.div>

        <div className="mt-14 space-y-10">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label className="overline mb-3 block text-[#4a4a4a]">{t("booking.name")}</label>
              <input data-testid="booking-name" className="ep-input" value={form.name} onChange={set("name")} placeholder={t("booking.namePh")} />
            </div>
            <div>
              <label className="overline mb-3 block text-[#4a4a4a]">{t("booking.email")}</label>
              <input data-testid="booking-email" type="email" className="ep-input" value={form.email} onChange={set("email")} placeholder={t("booking.emailPh")} />
            </div>
            <div>
              <label className="overline mb-3 block text-[#4a4a4a]">{t("booking.phone")}</label>
              <input data-testid="booking-phone" className="ep-input" value={form.phone} onChange={set("phone")} placeholder={t("booking.phonePh")} />
            </div>
            <div>
              <label className="overline mb-3 block text-[#4a4a4a]">{t("booking.company")}</label>
              <input data-testid="booking-company" className="ep-input" value={form.company} onChange={set("company")} placeholder={t("booking.companyPh")} />
            </div>
          </div>

          <div>
            <label className="overline mb-3 block text-[#4a4a4a]">{t("booking.idea")}</label>
            <textarea data-testid="booking-idea" className="ep-input min-h-[130px] resize-none" value={form.idea} onChange={set("idea")} placeholder={t("booking.ideaPh")} />
          </div>

          <div>
            <label className="overline mb-4 block text-[#4a4a4a]">{t("booking.chooseDate")}</label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
              {dates.map((d) => {
                const key = d.toISOString().slice(0, 10);
                const selected = date && date.toISOString().slice(0, 10) === key;
                return (
                  <button
                    key={key}
                    data-testid={`booking-date-${key}`}
                    onClick={() => setDate(d)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      selected ? "border-[#050505] bg-[#050505] text-white" : "border-black/8 bg-[#fafafa] text-[#050505] hover:border-black/30"
                    }`}
                  >
                    <span className="block text-xs font-light capitalize opacity-70">
                      {d.toLocaleDateString(locale, { weekday: "short" })}
                    </span>
                    <span className="font-display mt-1 block text-lg font-500">{d.getDate()}</span>
                    <span className="block text-xs font-light capitalize opacity-70">
                      {d.toLocaleDateString(locale, { month: "short" })}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="overline mb-4 block text-[#4a4a4a]">{t("booking.chooseTime")}</label>
            <div className="flex flex-wrap gap-3">
              {TIMES.map((tm) => (
                <button
                  key={tm}
                  data-testid={`booking-time-${tm}`}
                  onClick={() => setTime(tm)}
                  className={`rounded-full border px-6 py-3 text-sm font-light transition-all ${
                    time === tm ? "border-[#050505] bg-[#050505] text-white" : "border-black/8 bg-[#fafafa] text-[#050505] hover:border-black/30"
                  }`}
                >
                  {tm}
                </button>
              ))}
            </div>
          </div>

          {date && time && (
            <div className="flex items-center gap-3 rounded-2xl border border-black/8 bg-[#fafafa] px-5 py-4 text-sm font-light text-[#4a4a4a]">
              <Check size={16} className="text-[#0e7c6b]" />
              <span className="capitalize">{fmtDate(date)} · {time}</span>
            </div>
          )}

          <button
            data-testid="booking-submit-btn"
            disabled={!valid || loading}
            onClick={submit}
            className="ep-btn ep-btn-dark w-full disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> {t("booking.submitting")}</>
            ) : (
              t("booking.submit")
            )}
          </button>
          <p className="text-center text-xs font-light text-[#9a9a9a]">{t("booking.secure")}</p>
        </div>
      </div>

      <div className="mx-auto max-w-[680px] px-6 pb-24" data-testid="booking-info">
        <div className="border-t border-black/8 pt-12">
          <p className="overline mb-2 text-[#9a9a9a]">{t("method.overline")}</p>
          <h2 className="mb-6 text-base font-medium text-[#141514] md:text-lg">{t("method.title1")} {t("method.title2")}</h2>
          <div className="space-y-4">
            {(t("method.steps") || []).map((s, i) => (
              <div key={i} className="flex gap-3">
                <span className="mt-0.5 text-xs font-semibold text-[#0e7c6b]">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-sm font-light leading-relaxed text-[#3a3c39]"><strong className="font-medium text-[#141514]">{s.title}.</strong> {s.body}</p>
              </div>
            ))}
          </div>
          <p className="overline mb-2 mt-12 text-[#9a9a9a]">{t("faq.overline")}</p>
          <h2 className="mb-6 text-base font-medium text-[#141514] md:text-lg">{t("faq.title")}</h2>
          <div className="space-y-5">
            {(t("faq.items") || []).slice(0, 6).map((f, i) => (
              <div key={i}>
                <p className="mb-1 text-sm font-medium text-[#141514]">{f.q}</p>
                <p className="text-sm font-light leading-relaxed text-[#6b6f6c]">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
